import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Input,
  Alert,
  Spinner,
} from "reactstrap";
import Select2 from "react-select2-wrapper";
import "select2/dist/css/select2.min.css";
import { useTranslation } from "context/TranslationProvider";
import { useNotification } from "providers/NotificationProvider";

const REASON_CONFIGS = [
  { id: "Suspicious Activity", labelKey: "page.blockedIps.reasons.suspiciousActivity", defaultText: "Suspicious Activity" },
  { id: "Abuse / Malicious Traffic", labelKey: "page.blockedIps.reasons.maliciousTraffic", defaultText: "Abuse / Malicious Traffic" },
  { id: "Brute Force Attempt", labelKey: "page.blockedIps.reasons.bruteForce", defaultText: "Brute Force Attempt" },
  { id: "Bot / Automated Traffic", labelKey: "page.blockedIps.reasons.botTraffic", defaultText: "Bot / Automated Traffic" },
  { id: "Security Investigation", labelKey: "page.blockedIps.reasons.securityInvestigation", defaultText: "Security Investigation" },
  { id: "Other", labelKey: "page.blockedIps.reasons.other", defaultText: "Other" },
];

function isValidIpAddress(ip) {
  if (!ip || typeof ip !== "string") return false;
  const trimmed = ip.trim();
  const ipv4Regex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
  const ipv6Regex = /^(([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9]))$/;
  return ipv4Regex.test(trimmed) || ipv6Regex.test(trimmed) || trimmed === "::1";
}

const ManualBlockModal = ({ isOpen, toggle, selectedRow, onConfirm }) => {
  const { t, language } = useTranslation();
  const { notify } = useNotification();
  const modalBodyRef = useRef(null);

  const [ipAddress, setIpAddress] = useState("");
  const [blockType, setBlockType] = useState("TEMPORARY");
  const [durationMs, setDurationMs] = useState(String(15 * 60 * 1000));
  const [reason, setReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIpAddress(selectedRow?.ip_address || "");
      setBlockType("TEMPORARY");
      setDurationMs(String(15 * 60 * 1000));
      setReason("");
      setCustomReason("");
      setError("");
      setLoading(false);
    }
  }, [isOpen, selectedRow]);

  const reasonOptions = [
    { id: "", text: t("page.blockedIps.selectReason", "Select reason") },
    ...REASON_CONFIGS.map((r) => ({ id: r.id, text: t(r.labelKey, r.defaultText) })),
  ];

  const durationOptions = [
    { id: String(15 * 60 * 1000),      text: t("page.blockedIps.durations.15mLong", "15 Minutes") },
    { id: String(2 * 60 * 60 * 1000),  text: t("page.blockedIps.durations.2hLong", "2 Hours") },
    { id: String(24 * 60 * 60 * 1000), text: t("page.blockedIps.durations.24hLong", "24 Hours") },
    { id: String(7 * 24 * 60 * 60 * 1000), text: t("page.blockedIps.durations.7dLong", "7 Days") },
  ];

  const handleSubmit = async () => {
    if (!ipAddress.trim()) {
      const msg = t("page.blockedIps.validation.ipRequired", "IP Address is required.");
      setError(msg);
      notify("error", msg);
      return;
    }

    if (!isValidIpAddress(ipAddress.trim())) {
      const msg = t("page.blockedIps.validation.invalidIp", "Please enter a valid IP address.");
      setError(msg);
      notify("error", msg);
      return;
    }

    if (!reason) {
      const msg = t("page.blockedIps.validation.reasonRequired", "Please select a block reason.");
      setError(msg);
      notify("error", msg);
      return;
    }

    if (reason === "Other" && !customReason.trim()) {
      const msg = t("page.blockedIps.validation.customReasonRequired", "Please provide a reason when 'Other' is selected.");
      setError(msg);
      notify("error", msg);
      return;
    }

    const blockedUntil = blockType === "TEMPORARY"
      ? new Date(Date.now() + Number(durationMs)).toISOString()
      : null;

    setLoading(true);
    try {
      await onConfirm({
        ipAddress: ipAddress.trim(),
        blockType,
        reason,
        customReason: reason === "Other" ? customReason.trim() : undefined,
        blockedUntil,
      });
    } catch {
      notify("error", t("common.failed", "Failed to block IP"));
    } finally {
      setLoading(false);
    }
  };

  const isFormInvalid =
    !ipAddress.trim() ||
    !reason ||
    (reason === "Other" && !customReason.trim()) ||
    loading;

  return (
    <Modal
      isOpen={isOpen}
      toggle={loading ? undefined : toggle}
      size="md"
      className="modal-dialog-centered"
    >
      <ModalHeader toggle={loading ? undefined : toggle} className="border">
        {t("page.blockedIps.modal.blockTitle", "Block IP Address")}
      </ModalHeader>

      <ModalBody ref={modalBodyRef}>
        {error && <Alert color="danger" className="py-2 text-sm">{error}</Alert>}

        <FormGroup className="mb-3">
          <label className="form-control-label text-sm font-weight-600 mb-1" htmlFor="ipAddressInput">
            {t("table.headers.ipAddress", "IP Address")} <span className="text-danger">*</span>
          </label>
          <Input
            type="text"
            id="ipAddressInput"
            className="form-control"
            placeholder="e.g. 192.168.1.1"
            value={ipAddress}
            onChange={(e) => {
              setIpAddress(e.target.value);
              if (error) setError("");
            }}
            disabled={!!selectedRow?.ip_address || loading}
          />
        </FormGroup>

        <FormGroup className="mb-3">
          <label className="form-control-label text-sm font-weight-600 mb-1 d-block">
            {t("table.headers.blockType", "Block Type")}
          </label>
          <div className="d-flex align-items-center mt-1">
            <div className="custom-control custom-radio custom-control-inline mr-4">
              <input
                type="radio"
                id="blockTypeTemp"
                name="blockTypeRadio"
                className="custom-control-input"
                checked={blockType === "TEMPORARY"}
                onChange={() => setBlockType("TEMPORARY")}
                disabled={loading}
              />
              <label className="custom-control-label font-weight-500 cursor-pointer" htmlFor="blockTypeTemp">
                {t("page.blockedIps.types.temporary", "Temporary")}
              </label>
            </div>
            <div className="custom-control custom-radio custom-control-inline">
              <input
                type="radio"
                id="blockTypePerm"
                name="blockTypeRadio"
                className="custom-control-input"
                checked={blockType === "PERMANENT"}
                onChange={() => setBlockType("PERMANENT")}
                disabled={loading}
              />
              <label className="custom-control-label font-weight-500 cursor-pointer" htmlFor="blockTypePerm">
                {t("page.blockedIps.types.permanent", "Permanent")}
              </label>
            </div>
          </div>
        </FormGroup>

        {blockType === "TEMPORARY" && (
          <FormGroup className="mb-3">
            <label className="form-control-label text-sm font-weight-600 mb-1">
              {t("table.headers.duration", "Duration")}
            </label>
            <Select2
              key={`modal-duration-${language}`}
              className="form-control"
              value={durationMs}
              onChange={(e) => setDurationMs(e.target.value)}
              disabled={loading}
              options={{
                width: "100%",
                dropdownParent: modalBodyRef.current,
                data: durationOptions,
              }}
            />
          </FormGroup>
        )}

        {blockType === "PERMANENT" && (
          <Alert color="warning" className="text-sm mb-3">
            <strong>{t("common.warning", "Warning")}:</strong>{" "}
            {t(
              "page.blockedIps.permanentWarning",
              "Permanent blocks will not auto-expire and require explicit admin action to unblock."
            )}
          </Alert>
        )}

        <FormGroup className="mb-3">
          <label className="form-control-label text-sm font-weight-600 mb-1">
            {t("table.headers.reason", "Reason")} <span className="text-danger">*</span>
          </label>
          <Select2
            key={`modal-reason-${language}`}
            className="form-control"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (error) setError("");
            }}
            disabled={loading}
            options={{
              width: "100%",
              dropdownParent: modalBodyRef.current,
              data: reasonOptions,
            }}
          />
        </FormGroup>

        {reason === "Other" && (
          <FormGroup className="mb-2">
            <label className="form-control-label text-sm font-weight-600 mb-1" htmlFor="customReasonInput">
              {t("page.blockedIps.customReason", "Specify Reason")} <span className="text-danger">*</span>
            </label>
            <Input
              type="textarea"
              id="customReasonInput"
              className="form-control"
              rows={3}
              placeholder={t("page.blockedIps.customReasonPlaceholder", "Enter detailed reason...")}
              value={customReason}
              onChange={(e) => {
                setCustomReason(e.target.value);
                if (error) setError("");
              }}
              disabled={loading}
            />
          </FormGroup>
        )}
      </ModalBody>

      <ModalFooter className="border">
        <Button color="secondary" onClick={toggle} disabled={loading}>
          {t("common.cancel", "Cancel")}
        </Button>
        <Button color="danger" onClick={handleSubmit} disabled={isFormInvalid}>
          {loading && <Spinner size="sm" className="mr-2" />}
          {t("page.blockedIps.actions.block", "Block IP")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ManualBlockModal;
